/**
 * Company-mode Dashboard sections.
 *
 * Operates over the date range chosen in the Dashboard shell (single
 * month by default, but up to N months in the picker). Every average
 * / percentage normalizes against working days only — see
 * utils/workdays for what counts (excludes weekends, Italian public
 * holidays, admin-flagged closures).
 *
 * Prior-period deltas: same-length window immediately before the
 * current range. E.g. range = Mar 2026 → prior = Feb 2026. Range =
 * Jan–Mar 2026 → prior = Oct–Dec 2025.
 */
import { useMemo, useState } from "preact/hooks";
import type { JSX } from "preact";
import { state } from "../state/store";
import { activePeople } from "../utils/names";
import { MONTHS } from "../config";
import { STATUS_LABEL, type StatusCode } from "../types";
import { toast } from "../utils/toast";
import { ClosuresPanel } from "./ClosuresPanel";
import {
  STATUS_CODES,
  computeDays,
  computeDaysInRange,
  computeRangeAggregate,
  computeDayOfWeek,
  computeUnits,
  computeAttention,
  findEmptyCalendars,
  findHeavyTrip,
  workdayKeysFromDays,
  type DayCell,
  type RangeAggregate,
  type UnitStat,
} from "./dashboardMetrics";
import {
  priorRange,
  rangeLabel,
  rangeMonthCount,
  type DateRange,
} from "./Dashboard";

const STATUS_COLOR: Record<StatusCode, string> = {
  P: "var(--s-presence)",
  T: "var(--s-trip)",
  R: "var(--s-remote)",
  M: "var(--s-milan)",
  O: "var(--s-out)",
  A: "var(--s-available)",
  V: "var(--s-rovereto)",
};

const HEAVY_TRIP_THRESHOLD = 5;

function pct(v: number, digits = 0): string {
  return `${(v * 100).toFixed(digits)}%`;
}

function ppDelta(curr: number, prev: number): {
  text: string;
  dir: "up" | "down" | "flat";
} {
  const diff = (curr - prev) * 100;
  if (Math.abs(diff) < 0.5) return { text: "no change", dir: "flat" };
  return {
    text: `${diff > 0 ? "+" : ""}${diff.toFixed(1)} pp`,
    dir: diff > 0 ? "up" : "down",
  };
}

export function DashboardCompany({
  range,
}: {
  range: DateRange;
}): JSX.Element {
  const [exporting, setExporting] = useState(false);
  const s = state.value;
  const people = activePeople(s.roster);
  const totalPeople = people.length;
  const monthCount = rangeMonthCount(range);

  // Days across the chosen range
  const days = useMemo(
    () =>
      computeDaysInRange(
        range.startYear,
        range.startMonth,
        range.endYear,
        range.endMonth,
        people,
        s.calendar,
        s.closures,
      ),
    [range, people, s.calendar, s.closures],
  );
  const workdayKeys = useMemo(() => workdayKeysFromDays(days), [days]);
  const agg = useMemo(
    () => computeRangeAggregate(days, totalPeople),
    [days, totalPeople],
  );
  const dow = useMemo(
    () => computeDayOfWeek(days, totalPeople),
    [days, totalPeople],
  );
  const units = useMemo(
    () => computeUnits(workdayKeys, people, s.roster, s.calendar),
    [workdayKeys, people, s.roster, s.calendar],
  );

  // Prior-period aggregates for deltas
  const prior = useMemo(() => priorRange(range), [range]);
  const priorDays = useMemo(
    () =>
      computeDaysInRange(
        prior.startYear,
        prior.startMonth,
        prior.endYear,
        prior.endMonth,
        people,
        s.calendar,
        s.closures,
      ),
    [prior, people, s.calendar, s.closures],
  );
  const priorAgg = useMemo(
    () => computeRangeAggregate(priorDays, totalPeople),
    [priorDays, totalPeople],
  );
  // Only show prior-period deltas when the prior period has REAL data
  // to compare against. Without the filledCells guard, picking a range
  // that goes back before the test campaign started would produce
  // misleading "+30pp" arrows comparing current data to an empty
  // baseline. We require ≥10% coverage to call it a meaningful prior.
  const hasPrev =
    priorAgg.expectedCells > 0 &&
    priorAgg.filledCells > 0 &&
    priorAgg.coverage >= 0.1;

  // Today (only meaningful when today falls within the visible range)
  const today = useMemo(() => {
    const now = new Date();
    const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    return days.find((d) => d.key === key) ?? null;
  }, [days]);

  // Attention list inputs
  const emptyCalendars = useMemo(
    () => findEmptyCalendars(workdayKeys, people, s.calendar),
    [workdayKeys, people, s.calendar],
  );
  const heavyTrip = useMemo(
    () =>
      findHeavyTrip(workdayKeys, people, s.calendar, HEAVY_TRIP_THRESHOLD),
    [workdayKeys, people, s.calendar],
  );
  const attention = useMemo(
    () =>
      computeAttention(
        agg,
        units,
        dow,
        emptyCalendars.length,
        heavyTrip.length,
      ),
    [agg, units, dow, emptyCalendars.length, heavyTrip.length],
  );

  // Excel export — when the range spans multiple months, export each
  // month as its own block by using the first month's data (the export
  // contract is per-month). For a multi-month range we just export the
  // last (current) month; the dashboard UI is still the place to see
  // the cross-range view.
  const onExport = async (): Promise<void> => {
    setExporting(true);
    try {
      const exportYear = range.endYear;
      const exportMonth = range.endMonth;
      const monthDays = computeDays(
        exportYear,
        exportMonth,
        people,
        s.calendar,
        s.closures,
      );
      const monthAgg = computeRangeAggregate(monthDays, totalPeople);
      const { exportDashboardToExcel } = await import("./excelExport");
      await exportDashboardToExcel({
        year: exportYear,
        month: exportMonth,
        nDays: monthDays.length,
        people,
        daily: monthDays.map((d) => d.counts),
        monthTotals: monthAgg.statusTotals,
        roster: s.roster,
        calendar: s.calendar,
        notes: s.notes,
        meetings: s.meetings,
      });
      if (monthCount > 1) {
        toast(
          `Workbook downloaded (${MONTHS[exportMonth]} ${exportYear} only — the on-screen range stats stay in the app).`,
        );
      } else {
        toast("Workbook downloaded");
      }
    } catch (err) {
      console.error("export failed:", err);
      toast("Could not generate the workbook — check the console.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div class="dashboard-company">
      <div class="dash-export-row">
        <span class="dashboard-range-summary">
          Showing <b>{rangeLabel(range)}</b> · {agg.workingDays} working day{agg.workingDays === 1 ? "" : "s"}
        </span>
        <button
          class="dash-export-btn"
          disabled={exporting}
          onClick={() => void onExport()}
        >
          {exporting ? "Building workbook…" : "↓ Export to Excel"}
        </button>
      </div>

      <ClosuresPanel />

      <PulseCards
        today={today}
        agg={agg}
        prevAgg={priorAgg}
        hasPrev={hasPrev}
        workdaysCount={agg.workingDays}
        totalPeople={totalPeople}
        monthCount={monthCount}
      />

      <DailyOccupancyChart
        days={days}
        totalPeople={totalPeople}
        monthCount={monthCount}
      />

      <DayOfWeekPattern dow={dow} totalPeople={totalPeople} />

      <StatusMix agg={agg} days={days} />

      <UnitTable
        units={units}
        avgOccupancy={agg.avgOccupancy}
        avgCoverage={agg.coverage}
      />

      <AttentionList items={attention} />
    </div>
  );
}

/* ───── Pulse cards ───── */

interface PulseProps {
  today: DayCell | null;
  agg: RangeAggregate;
  prevAgg: RangeAggregate;
  hasPrev: boolean;
  workdaysCount: number;
  totalPeople: number;
  monthCount: number;
}

function PulseCards({
  today, agg, prevAgg, hasPrev, workdaysCount, totalPeople, monthCount,
}: PulseProps): JSX.Element {
  const todayOccupancy =
    today && totalPeople > 0 ? today.inOffice / totalPeople : 0;
  const occupancyDelta = ppDelta(agg.avgOccupancy, prevAgg.avgOccupancy);
  const remoteDelta = ppDelta(agg.avgRemoteShare, prevAgg.avgRemoteShare);
  const tripDelta = ppDelta(agg.avgTripShare, prevAgg.avgTripShare);
  const coverageDelta = ppDelta(agg.coverage, prevAgg.coverage);

  const prevLabel = monthCount === 1 ? "vs prior month" : "vs prior period";

  return (
    <div class="pulse-row">
      <PulseCard
        label={monthCount === 1 ? "Today" : "Snapshot"}
        value={today ? (today.isWorkday ? `${today.inOffice}` : "—") : "—"}
        sub={
          today
            ? today.isWorkday
              ? `${Math.round(todayOccupancy * 100)}% of ${totalPeople} in office`
              : `Non-working day${
                  today.closureLabel
                    ? ` · ${today.closureLabel}`
                    : today.holidayName
                      ? ` · ${today.holidayName}`
                      : today.isWeekend
                        ? " · weekend"
                        : ""
                }`
            : "Today is outside the selected range"
        }
        accent="var(--s-presence)"
      />
      <PulseCard
        label="Avg daily occupancy"
        value={pct(agg.avgOccupancy)}
        sub={`across ${workdaysCount} working day${workdaysCount === 1 ? "" : "s"}`}
        delta={hasPrev ? { ...occupancyDelta, ref: prevLabel } : undefined}
        accent="var(--blue)"
      />
      <PulseCard
        label="Remote share"
        value={pct(agg.avgRemoteShare)}
        sub="of person-workdays remote"
        delta={hasPrev ? { ...remoteDelta, ref: prevLabel } : undefined}
        accent="var(--s-remote)"
        invertDelta
      />
      <PulseCard
        label="Trip intensity"
        value={pct(agg.avgTripShare)}
        sub="of person-workdays on trip"
        delta={hasPrev ? { ...tripDelta, ref: prevLabel } : undefined}
        accent="var(--s-trip)"
        invertDelta
      />
      <PulseCard
        label="Schedule coverage"
        value={pct(agg.coverage)}
        sub={`${agg.filledCells.toLocaleString()} of ${agg.expectedCells.toLocaleString()} cells`}
        delta={hasPrev ? { ...coverageDelta, ref: prevLabel } : undefined}
        accent={
          agg.coverage >= 0.85
            ? "var(--s-presence)"
            : agg.coverage >= 0.6
              ? "var(--s-available)"
              : "var(--s-out)"
        }
      />
      <PulseCard
        label="Active people"
        value={`${totalPeople}`}
        sub="on the current active roster"
        accent="var(--blue-400)"
      />
    </div>
  );
}

interface PulseCardProps {
  label: string;
  value: string;
  sub: string;
  delta?: { text: string; dir: "up" | "down" | "flat"; ref: string };
  accent: string;
  invertDelta?: boolean;
}

function PulseCard({
  label, value, sub, delta, accent, invertDelta,
}: PulseCardProps): JSX.Element {
  const deltaCls =
    delta && delta.dir !== "flat"
      ? `pulse-delta pulse-delta-${invertDelta ? (delta.dir === "up" ? "down" : "up") : delta.dir}`
      : "pulse-delta pulse-delta-flat";
  return (
    <div class="pulse-card" style={`border-top:3px solid ${accent}`}>
      <div class="pulse-label">{label}</div>
      <div class="pulse-value">{value}</div>
      <div class="pulse-sub">
        <span>{sub}</span>
        {delta && (
          <span class={deltaCls} title={delta.ref}>
            {delta.dir === "up" ? "↑" : delta.dir === "down" ? "↓" : "·"} {delta.text}
          </span>
        )}
      </div>
    </div>
  );
}

/* ───── Daily occupancy chart ───── */

function DailyOccupancyChart({
  days, totalPeople, monthCount,
}: {
  days: DayCell[];
  totalPeople: number;
  monthCount: number;
}): JSX.Element {
  // Scale to the max occupancy across working days (or 100% if no data),
  // so the bars fill the chart space readably.
  const workdayMax = days.reduce(
    (acc, d) =>
      d.isWorkday && totalPeople > 0
        ? Math.max(acc, d.inOffice / totalPeople)
        : acc,
    0,
  );
  const ceil = workdayMax > 0 ? workdayMax : 1;
  // For multi-month ranges, drop the per-day numeric label (too crowded)
  // and show month dividers at the boundary between months.
  const dense = monthCount > 1;
  return (
    <div class="dash-card">
      <h3>
        Daily occupancy (% of active workforce in office)
        {monthCount > 1 && <span class="dash-card-sub"> — {monthCount} months</span>}
      </h3>
      <div class={`bar-chart${dense ? " bar-chart-dense" : ""}`}>
        {days.map((d, i) => {
          const occupancy =
            totalPeople > 0 ? d.inOffice / totalPeople : 0;
          const pctHeight = d.isWorkday
            ? Math.max((occupancy / ceil) * 100, occupancy > 0 ? 3 : 0)
            : 0;
          let cls = "bar";
          if (!d.isWorkday) cls += " bar-muted";
          // Month boundary divider — first day of each month except the
          // very first day in the range.
          const isNewMonth = dense && i > 0 && d.day === 1;
          const reason = d.holidayName
            ? ` · ${d.holidayName}`
            : d.closureLabel
              ? ` · ${d.closureLabel}`
              : d.isWeekend
                ? " · weekend"
                : "";
          const title = d.isWorkday
            ? `${d.day} ${MONTHS[d.month]}: ${d.inOffice} in office · ${Math.round(occupancy * 100)}%`
            : `${d.day} ${MONTHS[d.month]}${reason}`;
          return (
            <div
              key={`${d.year}-${d.month}-${d.day}`}
              class={`bar-col${isNewMonth ? " bar-col-divider" : ""}`}
            >
              <div class={cls} style={`height:${pctHeight}%`} title={title}>
                {!dense && (
                  <span class={`bar-val${occupancy > 0 ? "" : " zero"}`}>
                    {d.isWorkday && occupancy > 0
                      ? `${Math.round(occupancy * 100)}%`
                      : ""}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div class={`bar-labels${dense ? " bar-labels-dense" : ""}`}>
        {days.map((d) => (
          <div
            key={`${d.year}-${d.month}-${d.day}`}
            class={`lbl${d.isWorkday ? "" : " lbl-muted"}`}
          >
            {dense ? (d.day === 1 ? MONTHS[d.month]?.slice(0, 3) : "") : d.day}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───── Day-of-week pattern ───── */

function DayOfWeekPattern({
  dow, totalPeople,
}: {
  dow: ReturnType<typeof computeDayOfWeek>;
  totalPeople: number;
}): JSX.Element {
  const max = dow.reduce((acc, d) => Math.max(acc, d.avgOccupancy), 0) || 1;
  return (
    <div class="dash-card">
      <h3>Day-of-week pattern (avg in-office across working days)</h3>
      <div class="dow-chart">
        {dow.map((d) => {
          const heightPct = (d.avgOccupancy / max) * 100;
          return (
            <div key={d.wd} class="dow-col">
              <div class="dow-bar-track">
                <div
                  class="dow-bar"
                  style={`height:${heightPct}%`}
                  title={`${d.label}: ${d.avgInOffice.toFixed(1)} avg / ${totalPeople} (${pct(d.avgOccupancy)}) across ${d.workingDays} working day${d.workingDays === 1 ? "" : "s"}`}
                />
              </div>
              <div class="dow-pct">{pct(d.avgOccupancy)}</div>
              <div class="dow-label">{d.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ───── Status mix — daily evolution + summary ───── */

function StatusMix({
  agg, days,
}: {
  agg: RangeAggregate;
  days: DayCell[];
}): JSX.Element {
  const total =
    STATUS_CODES.reduce((acc, c) => acc + agg.statusTotals[c], 0) || 1;
  const workdays = days.filter((d) => d.isWorkday);
  return (
    <div class="dash-card">
      <h3>Status mix — daily evolution & range summary</h3>
      {workdays.length > 0 && (
        <>
          <div class="mix-evolution" aria-label="Daily status mix per working day">
            {workdays.map((d) => {
              const totalDay = d.filled || 1;
              return (
                <div
                  key={d.key}
                  class="mix-col"
                  title={`${d.day} ${MONTHS[d.month]}: ${d.filled} of ${agg.totalPeople} cells filled`}
                >
                  {STATUS_CODES.map((c) => {
                    const h = (d.counts[c] / totalDay) * 100;
                    return h > 0 ? (
                      <div
                        key={c}
                        class="mix-seg"
                        style={`height:${h}%;background:${STATUS_COLOR[c]}`}
                      />
                    ) : null;
                  })}
                </div>
              );
            })}
          </div>
        </>
      )}
      <div class="mix-summary-label">Range total (working days only):</div>
      <div class="stack-bar">
        {STATUS_CODES.map((c) => {
          const pctW = (agg.statusTotals[c] / total) * 100;
          return pctW > 0 ? (
            <div
              key={c}
              style={`width:${pctW}%;background:${STATUS_COLOR[c]}`}
              title={`${STATUS_LABEL[c]}: ${agg.statusTotals[c]} (${pct(pctW / 100, 1)})`}
            />
          ) : null;
        })}
      </div>
      <div class="legend-line">
        {STATUS_CODES.map((c) => (
          <span key={c}>
            <i style={`background:${STATUS_COLOR[c]}`} />
            {STATUS_LABEL[c]}: {agg.statusTotals[c]} ({pct(agg.statusShare[c])})
          </span>
        ))}
      </div>
    </div>
  );
}

/* ───── Unit table ───── */

type UnitSortKey =
  | "unit"
  | "headcount"
  | "inOfficeShare"
  | "remoteShare"
  | "tripShare"
  | "coverage";

function UnitTable({
  units, avgOccupancy, avgCoverage,
}: {
  units: UnitStat[];
  avgOccupancy: number;
  avgCoverage: number;
}): JSX.Element {
  const [sortKey, setSortKey] = useState<UnitSortKey>("inOfficeShare");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const sorted = [...units].sort((a, b) => {
    let cmp = 0;
    if (sortKey === "unit") cmp = a.unit.localeCompare(b.unit);
    else if (sortKey === "headcount") cmp = a.headcount - b.headcount;
    else cmp = (a[sortKey] as number) - (b[sortKey] as number);
    return sortDir === "asc" ? cmp : -cmp;
  });

  const onHeader = (k: UnitSortKey): void => {
    if (sortKey === k) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(k);
      setSortDir(k === "unit" ? "asc" : "desc");
    }
  };

  const arrow = (k: UnitSortKey): string =>
    sortKey === k ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  const maxInOffice = Math.max(1, ...units.map((u) => u.inOfficeShare));

  const rankBand = (values: number[], v: number): "top" | "bot" | null => {
    if (values.length < 4) return null;
    const sortedV = [...values].sort((a, b) => a - b);
    const q1 = sortedV[Math.floor(sortedV.length * 0.25)]!;
    const q3 = sortedV[Math.floor(sortedV.length * 0.75)]!;
    if (v >= q3) return "top";
    if (v <= q1) return "bot";
    return null;
  };

  const inOfficeVals = units.map((u) => u.inOfficeShare);
  const remoteVals = units.map((u) => u.remoteShare);
  const tripVals = units.map((u) => u.tripShare);
  const coverageVals = units.map((u) => u.coverage);

  return (
    <div class="dash-card">
      <h3>Attendance by unit (% of unit-workdays, sortable)</h3>
      <div class="unit-table-context">
        Avg occupancy <b>{pct(avgOccupancy)}</b> · coverage <b>{pct(avgCoverage)}</b>
      </div>
      <table class="unit-table unit-table-v2">
        <thead>
          <tr>
            <th class="th-sort" onClick={() => onHeader("unit")}>
              Unit{arrow("unit")}
            </th>
            <th class="th-sort num" onClick={() => onHeader("headcount")}>
              People{arrow("headcount")}
            </th>
            <th class="th-sort num" onClick={() => onHeader("inOfficeShare")}>
              In office %{arrow("inOfficeShare")}
            </th>
            <th class="th-sort num" onClick={() => onHeader("remoteShare")}>
              Remote %{arrow("remoteShare")}
            </th>
            <th class="th-sort num" onClick={() => onHeader("tripShare")}>
              Trip %{arrow("tripShare")}
            </th>
            <th class="th-sort num" onClick={() => onHeader("coverage")}>
              Coverage %{arrow("coverage")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((u) => {
            const inOffBand = rankBand(inOfficeVals, u.inOfficeShare);
            const remoteBand = rankBand(remoteVals, u.remoteShare);
            const tripBand = rankBand(tripVals, u.tripShare);
            const covBand = rankBand(coverageVals, u.coverage);
            const barW = Math.round((u.inOfficeShare / maxInOffice) * 80);
            return (
              <tr key={u.unit}>
                <td>
                  <b>{u.unit}</b>
                </td>
                <td class="num">{u.headcount}</td>
                <td class={`num band-${inOffBand ?? "neutral"}`}>
                  <span class="mini-bar" style={`width:${barW}px`} />
                  {pct(u.inOfficeShare)}
                </td>
                <td class={`num band-${remoteBand ?? "neutral"}`}>
                  {pct(u.remoteShare)}
                </td>
                <td class={`num band-${tripBand ?? "neutral"}`}>
                  {pct(u.tripShare)}
                </td>
                <td class={`num band-${covBand ?? "neutral"}`}>
                  {pct(u.coverage)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ───── Attention list ───── */

function AttentionList({
  items,
}: {
  items: ReturnType<typeof computeAttention>;
}): JSX.Element | null {
  if (items.length === 0) {
    return (
      <div class="dash-card attention-card">
        <h3>Attention</h3>
        <div class="attention-empty">
          ✓ No anomalies — coverage looks healthy and patterns are even.
        </div>
      </div>
    );
  }
  return (
    <div class="dash-card attention-card">
      <h3>Attention</h3>
      <ul class="attention-list">
        {items.map((item, i) => (
          <li key={i} class={`attention-item attention-${item.severity}`}>
            <span class="attention-dot" aria-hidden="true" />
            <span>{item.message}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
